import React from 'react';
import PropTypes from 'prop-types';

import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import i18n from 'i18n-js';
import { Table } from 'components/UI';
import Edit from '@mui/icons-material/Edit';
import VersionStatus from './VersionStatus';
import DeleteIcon from '@mui/icons-material/Delete';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';

const DefaultVersionIcon = styled(StarOutlinedIcon)({
  color: '#FFB027',
});

const ActionIcon = styled(StarBorderOutlinedIcon)({
  color: '#777777',
});

const DocumentVersionsTable = ({
  page,
  limit,
  versions,
  handleEdit,
  handleDelete,
  defaultSortAsc,
  defaultSortField,
  handlePageChange,
  handleMarkDefault,
  loading,
  pagination,
  handleRowClick,
  handleSort,
  handlePerRowsChange,
}) => {
  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: 'id',
      sortable: true,
      grow: 0,
    },
    {
      name: i18n.t('tableColumns.name'),
      selector: (row) => (row?.version ? row.version : '--'),
    },
    {
      name: i18n.t('tableColumns.status'),
      selector: (row) => (row?.status ? <VersionStatus status={row?.status} /> : '--'),
    },
    {
      name: i18n.t('tableColumns.file'),
      selector: (row) => (row?.files?.length > 0 ? row.files[0]?.filename : '--'),
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.action'),
      selector: (row) => {
        return (
          <Box display="flex">
            <IconButton aria-label="edit" onClick={() => handleEdit(row)}>
              <Edit fontSize="inherit" />
            </IconButton>
            <IconButton aria-label="delete" onClick={() => handleDelete(row.id)}>
              <DeleteIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              aria-label="mark-default"
              disabled={row.default}
              onClick={() => handleMarkDefault(row)}
            >
              {row.default ? (
                <DefaultVersionIcon fontSize="inherit" />
              ) : (
                <ActionIcon fontSize="inherit" />
              )}
            </IconButton>
          </Box>
        );
      },
      grow: 1,
    },
  ];

  return (
    <Paper elevation={2}>
      <Table
        page={page}
        limit={limit}
        data={versions}
        columns={columns}
        loading={loading}
        onSort={handleSort}
        defaultSortAsc={defaultSortAsc}
        handleRowClicked={handleRowClick}
        defaultSortField={defaultSortField}
        handlePageChange={handlePageChange}
        totalRows={pagination?.totalRows || 0}
        handlePerRowsChange={handlePerRowsChange}
      />
    </Paper>
  );
};

DocumentVersionsTable.propTypes = {
  versions: PropTypes.array,
  page: PropTypes.number,
  limit: PropTypes.number,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  defaultSortAsc: PropTypes.bool,
  defaultSortField: PropTypes.string,
  handlePageChange: PropTypes.func,
  handleMarkDefault: PropTypes.func,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    totalRows: PropTypes.number,
  }),
  handleRowClick: PropTypes.func,
  handleSort: PropTypes.func,
  handlePerRowsChange: PropTypes.func,
};

DocumentVersionsTable.defaultProps = {
  versions: [],
  page: 1,
  limit: 10,
  handleEdit: () => {},
  handleDelete: () => {},
  defaultSortAsc: true,
  defaultSortField: '',
  handlePageChange: () => {},
  handleMarkDefault: () => {},
  loading: false,
  pagination: { totalRows: 0 },
  handleRowClick: () => {},
  handleSort: () => {},
  handlePerRowsChange: () => {},
};

export default DocumentVersionsTable;
